/**
 * 取得統一的 cookie 選項
 * 確保所有 cookie 都使用相同的 domain 設定邏輯
 *
 * @param options 額外的 cookie 選項（如 default, maxAge, sameSite 等）
 * @returns 統一的 cookie 選項，與 Nuxt 的 CookieOptions 類型兼容
 */
export const useCookieOptions = <T = string>(
  options: {
    default?: () => T;
    maxAge?: number;
    sameSite?: 'strict' | 'lax' | 'none';
    path?: string;
  } = {}
): {
  default?: () => T;
  domain?: string;
  maxAge?: number;
  sameSite?: 'strict' | 'lax' | 'none';
  path?: string;
} => {
  /**
   * 取得當前網域的根域名
   * @param host 主機名稱（例如：app.momentx.ai）
   * @returns 根域名（例如：.momentx.ai），如果是 localhost 或 IP 則返回空字串
   */
  const getRootDomain = (host: string): string => {
    // localhost 或 IP 地址不需要設定 domain（返回空字串表示使用當前域名）
    if (host === 'localhost' || host === '127.0.0.1' || /^\d+\.\d+\.\d+\.\d+$/.test(host)) {
      return '';
    }
    // 分割域名部分
    const parts = host.split('.');
    // 如果是二級域名（如 momentx.ai），返回 .momentx.ai
    // 如果是三級域名（如 app.momentx.ai），也返回 .momentx.ai
    if (parts.length >= 2) {
      return `.${parts.slice(-2).join('.')}`;
    }
    return '';
  };

  const result: {
    default?: () => T;
    domain?: string;
    maxAge?: number;
    sameSite?: 'strict' | 'lax' | 'none';
    path?: string;
  } = {};

  // 只在有提供 default 時才設定
  if (options.default) {
    result.default = options.default;
  }

  // 只在客戶端設定 domain
  if (import.meta.client) {
    const domain = getRootDomain(window.location.hostname);
    if (domain) {
      result.domain = domain;
    }
  }

  // 只在有提供時才設定這些選項
  if (options.maxAge !== undefined) {
    result.maxAge = options.maxAge;
  }
  if (options.sameSite !== undefined) {
    result.sameSite = options.sameSite;
  }
  // path 預設為 '/'，但如果明確設定了就使用設定的值
  result.path = options.path ?? '/';

  return result;
};
